// Predictive search with SKU support + correct routing using data URLs from Liquid
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    // ABSOLUTE URLs provided by Liquid
    this.SEARCH_URL   = this.dataset.searchUrl   || '/search';
    this.SUGGEST_URL  = this.dataset.suggestUrl  || '/search/suggest.json';

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Enter in the input (form submit)
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    // Click on search icon/button -> ALWAYS go to /search
    this.searchButton = this.input.form.querySelector('button.search__button');
    if (this.searchButton) {
      this.searchButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.navigateToSearchPage(this.getQuery());
      });
    }

    // Click on “Search for …” row -> ALWAYS go to /search (handles link or button variants)
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.addEventListener('click', (e) => {
        const el = e.target.closest(
          '[data-predictive-search-search-for-button], ' +
          '[data-predictive-search-search-for-text], ' +
          '.predictive-search__item--term, ' +
          '.predictive-search__item[aria-label^="Search for"], ' +
          'button.predictive-search__item'
        );
        if (!el) return;
        e.preventDefault();
        e.stopPropagation();
        this.navigateToSearchPage(this.getQuery());
      });
    }

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  // Build and navigate to the search page with product-only filter and optional prefix
  navigateToSearchPage(q) {
    const params = new URLSearchParams();
    params.set('type', 'product');
    const prefixInput = this.input.form.querySelector('input[name="options[prefix]"]');
    if (prefixInput && prefixInput.value) params.set('options[prefix]', prefixInput.value);
    params.set('q', q || '');
    const url = new URL(this.SEARCH_URL, window.location.origin);
    url.search = params.toString();
    window.location.assign(url.toString());
  }

  // ENTER in the field: SKU-first unless a suggestion is highlighted
  onFormSubmit(event) {
    const q = this.getQuery();
    if (!q.length) {
      event.preventDefault();
      return;
    }

    // If a suggestion is highlighted, open it (keyboard UX)
    const selectedAnchor = this.querySelector('[aria-selected="true"] a');
    if (selectedAnchor) {
      event.preventDefault();
      selectedAnchor.click();
      return;
    }

    // Plain Enter: try SKU-first
    const looksLikeSku =
      q.split(/\s+/).length === 1 &&
      /^[A-Za-z0-9][A-Za-z0-9._\/-]*$/.test(q) &&
      /[\d-]/.test(q);

    const params = new URLSearchParams();
    params.set('type', 'product');
    const prefixInput = this.input.form.querySelector('input[name="options[prefix]"]');
    if (prefixInput && prefixInput.value) params.set('options[prefix]', prefixInput.value);

    if (looksLikeSku && !/^sku:/i.test(q)) {
      params.set('oq', q);          // results-page fallback
      params.set('q', 'sku:' + q);  // SKU attempt
    } else {
      params.set('q', q);
    }

    event.preventDefault();
    const url = new URL(this.SEARCH_URL, window.location.origin);
    url.search = params.toString();
    window.location.assign(url.toString());
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const el = this.querySelector('[data-predictive-search-search-for-text]');
    const current = el?.innerText;
    if (!current) return;

    if (!previousTerm) {
      el.innerText = current.replace(newTerm, newTerm);
      return;
    }

    const safePrev = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const matches = current.match(new RegExp(safePrev, 'g'));
    if (matches && matches.length > 1) return;

    el.innerText = current.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selected = this.querySelector('[aria-selected="true"]');

    const allVisible = Array.from(
      this.querySelectorAll('li, button.predictive-search__item')
    ).filter((el) => el.offsetParent !== null);

    let activeIndex = 0;
    if (moveUp && !selected) return;

    let selectedIndex = -1;
    for (let i = 0; selectedIndex === -1 && i <= allVisible.length; i++) {
      if (allVisible[i] === selected) selectedIndex = i;
    }

    this.statusElement && (this.statusElement.textContent = '');

    if (!moveUp && selected) {
      activeIndex = selectedIndex === allVisible.length - 1 ? 0 : selectedIndex + 1;
    } else if (moveUp) {
      activeIndex = selectedIndex === 0 ? allVisible.length - 1 : selectedIndex - 1;
    }

    if (activeIndex === selectedIndex) return;

    const activeEl = allVisible[activeIndex];
    activeEl.setAttribute('aria-selected', true);
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeEl.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Products only; include variants.sku so SKU terms match in predictive
    const url = new URL(this.SUGGEST_URL, window.location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,variants.sku,vendor'
    );
    url.searchParams.set('section_id', 'predictive-search');

    fetch(url.toString(), { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = section ? section.innerHTML : '';

        // sync cache
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) return; // aborted
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText || '');
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countNode = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countNode) this.setLiveRegionText(countNode.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - (document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0);
    return this.resultsMaxHeight;
  }

  open() {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    if (this.predictiveSearchResults) this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);